import global from '../global';
import {showError, showSuccess} from "../utils/helper";

/**
 * Check if thermal printer is connected and working
 * @returns {Promise<{connected: boolean, status: string, error: string|null}>}
 */
export const checkPrinterConnection = () => {
    return new Promise((resolve) => {
        try {
            // Check if printer functions are available
            if (typeof window.checkPrinterStatus !== 'function' ||
                typeof window.requestPrint !== 'function' ||
                typeof window.getPosData !== 'function') {
                resolve({
                    connected: false,
                    status: 'Printer funkcije nisu dostupne',
                    error: 'Printer driver nije učitan. Provjerite je li POS aplikacija pokrenuta.'
                });
                return;
            }

            // Try to get printer status
            let status;
            try {
                status = window.checkPrinterStatus();
            } catch (e) {
                resolve({
                    connected: false,
                    status: 'Greška pri provjeri statusa',
                    error: e.message
                });
                return;
            }

            // Send empty print job to actually test connection
            var p_name = "Printer1";
            var testTimeout = null;
            var resolved = false;

            function handleResult(result) {
                if (resolved) return;
                resolved = true;
                if (testTimeout) clearTimeout(testTimeout);

                // Check result for errors
                if (result && typeof result === 'string') {
                    const lowerResult = result.toLowerCase();
                    if (lowerResult.includes('error') ||
                        lowerResult.includes('offline') ||
                        lowerResult.includes('not found') ||
                        lowerResult.includes('disconnected') ||
                        lowerResult.includes('failed')) {
                        resolve({
                            connected: false,
                            status: 'Printer nije dostupan',
                            error: result
                        });
                        return;
                    }
                }

                resolve({
                    connected: true,
                    status: status || 'Printer je spreman',
                    error: null
                });
            }

            // Set timeout for response - if no response in 3 seconds, printer likely not connected
            testTimeout = setTimeout(() => {
                if (resolved) return;
                resolved = true;
                resolve({
                    connected: false,
                    status: 'Printer ne odgovara',
                    error: 'Timeout - printer možda nije spojen ili je isključen'
                });
            }, 3000);

            // Send minimal status check
            window.setPosId(9998);
            window.printText("", 0, 0, false, false, false, 0, 0);
            var strSubmit = window.getPosData();
            window.requestPrint(p_name, strSubmit, handleResult);

        } catch (e) {
            resolve({
                connected: false,
                status: 'Nije moguće provjeriti printer',
                error: e.message
            });
        }
    });
};

/**
 * Print a test page on thermal printer
 * @returns {Promise<{success: boolean, error: string|null}>}
 */
export const printTestPage = () => {
    return new Promise((resolve) => {
        try {
            if (typeof window.printText !== 'function' ||
                typeof window.requestPrint !== 'function' ||
                typeof window.getPosData !== 'function') {
                resolve({
                    success: false,
                    error: 'Printer funkcije nisu dostupne. Provjerite je li POS aplikacija pokrenuta.'
                });
                return;
            }

            var p_name = "Printer1";
            var issueID = 9999;
            var printTimeout = null;
            var resolved = false;

            function viewResult(result) {
                if (resolved) return;
                resolved = true;
                if (printTimeout) clearTimeout(printTimeout);

                // Check for error responses
                if (result && typeof result === 'string') {
                    const lowerResult = result.toLowerCase();
                    if (lowerResult.includes('error') ||
                        lowerResult.includes('offline') ||
                        lowerResult.includes('not found') ||
                        lowerResult.includes('disconnected') ||
                        lowerResult.includes('failed') ||
                        lowerResult.includes('unable')) {
                        resolve({ success: false, error: result });
                        return;
                    }
                }

                // Empty result or success
                resolve({ success: true, error: null });
            }

            // Set timeout - if no response in 5 seconds, assume printer not connected
            printTimeout = setTimeout(() => {
                if (resolved) return;
                resolved = true;
                resolve({
                    success: false,
                    error: 'Printer ne odgovara. Provjerite je li printer uključen i spojen.'
                });
            }, 5000);

            window.setPosId(issueID);

            // Print test page content
            window.printText("\n", 0, 0, false, false, false, 0, 1);
            window.printText("==========================================", 0, 0, false, false, false, 0, 1);
            window.printText("\n", 0, 0, false, false, false, 0, 1);
            window.printText("        TESTNI ISPIS PRINTERA", 0, 0, true, false, false, 0, 1);
            window.printText("\n", 0, 0, false, false, false, 0, 1);
            window.printText("==========================================", 0, 0, false, false, false, 0, 1);
            window.printText("\n\n", 0, 0, false, false, false, 0, 1);

            // Print device info if available
            if (global.invoiceDevice?.invoice_space_name) {
                window.printText("Blagajna: " + global.invoiceDevice.invoice_space_name, 0, 0, false, false, false, 0, 1);
                window.printText("\n", 0, 0, false, false, false, 0, 1);
            }

            // Print date/time
            const now = new Date();
            const dateStr = now.toLocaleDateString('hr-HR') + ' ' + now.toLocaleTimeString('hr-HR');
            window.printText("Datum: " + dateStr, 0, 0, false, false, false, 0, 1);
            window.printText("\n\n", 0, 0, false, false, false, 0, 1);

            // Print character test
            window.printText("Test znakova:", 0, 0, true, false, false, 0, 0);
            window.printText("\n", 0, 0, false, false, false, 0, 0);
            window.printText("ABCDEFGHIJKLMNOPQRSTUVWXYZ", 0, 0, false, false, false, 0, 0);
            window.printText("\n", 0, 0, false, false, false, 0, 0);
            window.printText("abcdefghijklmnopqrstuvwxyz", 0, 0, false, false, false, 0, 0);
            window.printText("\n", 0, 0, false, false, false, 0, 0);
            window.printText("0123456789", 0, 0, false, false, false, 0, 0);
            window.printText("\n", 0, 0, false, false, false, 0, 0);
            window.printText("ČčĆćŽžŠšĐđ", 0, 0, false, false, false, 0, 0);
            window.printText("\n\n", 0, 0, false, false, false, 0, 0);

            // Print line test
            window.printText("Test linija:", 0, 0, true, false, false, 0, 0);
            window.printText("\n", 0, 0, false, false, false, 0, 0);
            window.printText("------------------------------------------", 0, 0, false, false, false, 0, 0);
            window.printText("\n", 0, 0, false, false, false, 0, 0);
            window.printText("==========================================", 0, 0, false, false, false, 0, 0);
            window.printText("\n\n", 0, 0, false, false, false, 0, 1);

            window.printText("Printer radi ispravno!", 0, 0, true, false, false, 0, 1);
            window.printText("\n\n\n", 0, 0, false, false, false, 0, 0);

            window.cutPaper(1);

            var strSubmit = window.getPosData();
            window.requestPrint(p_name, strSubmit, viewResult);

        } catch (e) {
            resolve({
                success: false,
                error: e.message
            });
        }
    });
};

export const printReceipt = (data) => {
    console.log("print invoice: ")
    //console.log(data)
    /*try {
        window.setCharacterSet(1252); // Western Europe (Windows Latin-1)
    } catch (e) {
        console.warn("Nije moguće postaviti character set: ", e);
    }*/
    var LF = "\n";
    var issueID = 1;
    var tmp = "123456789"
    var _inch = 3;
    var p_name = "Printer1"
    var p_result = ""

    /*if(!data.invoice_real_id) {
        showError('Došlo je do greške prilikom ispisa računa! ID: ' + data.sale_id + "\nPonovo učitajte blagajnu i ispišite zadnji račun!")
        return;
    }*/


    console.log(data);

    function viewResult(result) {
        p_result = result;
        if(result != "") {
            console.log(result)
            if(!result.includes("success")) {
                showError('Došlo je do greške prilikom ispisa računa! Error: ' + result)
            }
        }

    }

    window.setPosId(issueID);
    window.checkPrinterStatus();
    var invoiceLogo = localStorage.getItem('invoiceLogo');
    //console.log(global.invoiceDevice)
    //console.log(invoiceLogo)

    //var imageFileWindows = "C:\\pos\\logo.png";
    //printBitmapFile(imageFileWindows,-2,1,false);
    if (invoiceLogo) {
        window.printBitmap(invoiceLogo, -2, 1, false);
    }

    window.printText("\n" + global.invoiceDevice.invoice_space_company, 0, 0, true, false, false, 0, 1);

    window.printText("\n" + global.invoiceDevice.invoice_space_address + ", " + global.invoiceDevice.invoice_space_zip + ", " + global.invoiceDevice.invoice_space_city, 0, 0, false, false, false, 0, 1);
    window.printText("\nOIB: " + global.invoiceDevice.invoice_space_oib, 0, 0, false, false, false, 0, 1);
    window.printText("\nBroj telefona: " + global.invoiceDevice.invoice_space_phone, 0, 0, false, false, false, 0, 1);
    window.printText("\nEmail: " + global.invoiceDevice.invoice_space_email, 0, 0, false, false, false, 0, 1);
    window.printText("\n" + global.invoiceDevice.invoice_space_web, 0, 0, false, false, false, 0, 1);
    window.printText("\n------------------------------------------", 0, 0, false, false, false, 0, 0);
    window.printText("\n" + global.invoiceDevice.invoice_space_name2, 0, 0, true, false, false, 0, 1);
    window.printText("\n" + global.invoiceDevice.invoice_space_address2 + ", " + global.invoiceDevice.invoice_space_zip2 + ", " + global.invoiceDevice.invoice_space_city2, 0, 0, false, false, false, 0, 1);
    window.printText("\n------------------------------------------\n\n", 0, 0, false, false, false, 0, 0);
    window.printText("Artikl               Kol.  Cijena    Iznos\n", 0, 0, false, false, false, 0, 0);
    window.printText("------------------------------------------\n", 0, 0, false, false, false, 0, 0);

    data.invoice_items.map(invoice_item => {
        window.printText(invoice_item.item_name + "\n", 0, 0, false, false, false, 0, 0);
        window.printText("                 " + invoice_item.item_quantity.padStart(6, " ") + "    " + invoice_item.item_price_with_tax.padStart(6, " ") + "   " + invoice_item.total_item_price_with_tax.padStart(6, " ") + "\n", 0, 0, false, false, false, 0, 0);

    })


    window.printText("------------------------------------------\n", 0, 0, false, false, false, 0, 0);

    if (data.invoice_total_discounts_with_tax != "0,00") {
        window.printText("Popust                " + data.invoice_total_discounts_with_tax.padStart(20, " ") + "\n", 0, 0, false, false, false, 0, 0);
        window.printText("------------------------------------------\n", 0, 0, false, false, false, 0, 0);
    }

    window.printText("Ukupno           " + data.invoice_total_with_tax.padStart(20, " ") + " " + data.invoice_currency_symbol + "\n", 0, 1, true, false, false, 0, 0);
    window.printText("------------------------------------------\n", 0, 0, false, false, false, 0, 0);
    window.printText("Način plaćanja: " + data.invoice_payment_method + "\n", 0, 0, false, false, false, 0, 0);
    window.printText("------------------------------------------\n", 0, 0, false, false, false, 0, 0);
    window.printText("Porez          %       Osnovica      Iznos\n", 0, 0, false, false, false, 0, 0);
    window.printText("------------------------------------------\n", 0, 0, false, false, false, 0, 0);

    data.invoice_tax_rates.map(tax_rate => {
        window.printText("PDV            " + tax_rate.Stopa + "     " + tax_rate.Osnovica.padStart(7, " ") + "      " + tax_rate.Iznos.padStart(7, " ") + "\n", 0, 0, false, false, false, 0, 0);
    })

    window.printText("------------------------------------------\n", 0, 0, false, false, false, 0, 0);
    if(data.note !== null && data.note !== "") {
        window.printText("\nNapomena: " + data.note, 0, 0, false, false, false, 0, 0);
        window.printText("\n------------------------------------------\n", 0, 0, false, false, false, 0, 0);
    }
    if(data.invoice_company !== null && data.invoice_company !== "") {
        var invoiceTypeLabel = data.type === 'E' ? 'E-račun:' : 'Račun R1';
        window.printText("\n" + invoiceTypeLabel + " " + data.invoice_real_id, 0, 0, false, false, false, 0, 0);
        window.printText("\n" + data.invoice_company, 0, 0, false, false, false, 0, 0);
        window.printText("\nOIB: " + data.invoice_oib, 0, 0, false, false, false, 0, 0);
        window.printText("\n" + data.invoice_address, 0, 0, false, false, false, 0, 0);
        window.printText("\n" + data.invoice_city, 0, 0, false, false, false, 0, 0);
        window.printText("\n" + data.invoice_zip, 0, 0, false, false, false, 0, 0);
        //window.printText("\n" + data.invoice_phone, 0, 0, false, false, false, 0, 0);
        window.printText("\n" + data.invoice_country, 0, 0, false, false, false, 0, 0);
        window.printText("\n------------------------------------------\n", 0, 0, false, false, false, 0, 0);
    } else {
        window.printText("\n\n" + global.invoiceDevice.invoice_title + " " + data.invoice_real_id, 0, 0, false, false, false, 0, 0);
    }

    window.printText("\nVrijeme računa: " + data.invoice_date, 0, 0, false, false, false, 0, 0);
    window.printText("\nOznaka blagajnika: " + data.admin_device_name, 0, 0, false, false, false, 0, 0);

    if(data.type === 'E') {
        window.printText("\n\neRačun je poslan preko posrednika za \neRačune.Ovo je samo vizualni prikaz računa\nOriginalni eRačun preuzmite kroz Vasu \naplikaciju za eRačune.\n\n", 0, 0, false, false, false, 0, 0);
    } else if(data.invoice_jir) {
        window.printText("\nZKI: " + data.invoice_zki, 0, 0, false, false, false, 0, 0);
        window.printText("\nJIR: " + data.invoice_jir + "\n\n", 0, 0, false, false, false, 0, 0);
        window.printQRCode(data.invoice_fiskal_qr_link, 0, 1, 7, 0);
    }

    // Loyalty section
    if (data.loyalty_member_id && data.loyalty_member_name) {
        window.printText("\n------------------------------------------\n", 0, 0, false, false, false, 0, 0);
        window.printText("KLUB VJERNOSTI\n", 0, 0, true, false, false, 0, 1);
        window.printText("Clan: " + data.loyalty_member_name + " (" + (data.loyalty_member_card || '') + ")\n", 0, 0, false, false, false, 0, 0);
        if (data.loyalty_points_spent && data.loyalty_points_spent > 0) {
            window.printText("Iskoristeno: -" + (data.loyalty_discount_amount || '0,00') + " EUR (" + data.loyalty_points_spent + " bodova)\n", 0, 0, false, false, false, 0, 0);
        }
        if (data.loyalty_points_earned && data.loyalty_points_earned > 0) {
            window.printText("Zaradjeno: +" + data.loyalty_points_earned.toFixed(2).replace('.', ',') + " bodova\n", 0, 0, false, false, false, 0, 0);
        }
        if (data.loyalty_points_balance !== undefined && data.loyalty_points_balance !== null) {
            window.printText("Novi saldo: " + data.loyalty_points_balance.toFixed(2).replace('.', ',') + " bodova\n", 0, 0, false, false, false, 0, 0);
        }
    }

    window.printText("\n\n", 0, 0, false, false, false, 0, 0);
    window.printText("Naknadno izdavanje kopije racuna nije\n moguce! Reklamacije ne uvazavamo bez\n predocenja racuna.", 0, 0, false, false, false, 0, 1);
    window.printText("\n\n", 0, 0, false, false, false, 0, 0);
    window.printText("Zahvaljujemo na ukazanom povjernju i vidimo se opet!!!", 0, 0, false, false, false, 0, 1);
    window.printText("\n\n", 0, 0, false, false, false, 0, 0);

    window.cutPaper(1);

    var strSubmit = window.getPosData();

    //console.log(strSubmit);

    window.requestPrint(p_name, strSubmit, viewResult);

    return true;
}